import { useState } from "react";
import { Download, Upload, Database, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EXPORTS = [
  { key: "clients", label: "Clients", module: "dataio", action: "export" },
  { key: "matters", label: "Matters", module: "dataio", action: "export" },
  { key: "time", label: "Time entries", module: "dataio", action: "export" },
  { key: "invoices", label: "Invoices", module: "finance", action: "export" },
];

export default function DataManager() {
  const { can } = useAuth();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doExport = async (entity) => {
    try {
      const res = await api.get(`/admin/dataio/export/${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `apexpath-${entity}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success(`${entity} exported`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const doImport = async (entity) => {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".csv";
    inp.onchange = async () => {
      setBusy(true); setResult(null);
      const fd = new FormData(); fd.append("file", inp.files[0]);
      try { const { data } = await api.post(`/admin/dataio/import/${entity}`, fd); setResult({ entity, ...data }); toast.success(`Imported ${data.created} ${entity}`); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
      finally { setBusy(false); }
    };
    inp.click();
  };

  const canExport = can("dataio", "export") || can("finance", "export");
  const canImport = can("dataio", "create");

  return (
    <div data-testid="data-manager">
      <div className="mb-6"><h1 className="font-serif text-3xl font-bold text-navy">Import &amp; Export</h1><p className="mt-1 text-sm text-slate-500">Securely export and import records. Respects your role permissions and is written to the audit trail.</p></div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2 text-navy"><Download size={18} /><h2 className="font-serif text-lg font-semibold">Export (CSV)</h2></div>
          {!canExport && <p className="text-sm text-slate-400">You do not have export permission.</p>}
          <div className="space-y-2">
            {EXPORTS.filter((e) => can(e.module, e.action)).map((e) => (
              <button key={e.key} onClick={() => doExport(e.key)} data-testid={`export-${e.key}`} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-navy hover:bg-slate-50">
                <span className="flex items-center gap-2"><FileSpreadsheet size={16} className="text-gold" /> {e.label}</span>
                <Download size={15} className="text-slate-400" />
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2 text-navy"><Upload size={18} /><h2 className="font-serif text-lg font-semibold">Import (CSV)</h2></div>
          {!canImport ? <p className="text-sm text-slate-400">You do not have import permission.</p> : (
            <div className="space-y-2">
              <button onClick={() => doImport("clients")} disabled={busy} data-testid="import-clients" className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-navy hover:bg-slate-50 disabled:opacity-60">
                <span className="flex items-center gap-2"><Database size={16} className="text-gold" /> Import clients</span><Upload size={15} className="text-slate-400" />
              </button>
              <p className="text-xs text-slate-400">CSV columns: first_name, last_name, email, phone, whatsapp, comm_preference, nationality, status. Existing emails are skipped.</p>
            </div>
          )}
          {result && (
            <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm" data-testid="import-result">
              <div className="font-medium text-navy">Imported {result.created} · skipped {result.skipped}</div>
              {result.errors?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-red-600">{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
